/**
 * Pillar 7 — Demo Admin surface.
 *
 * Platform-admin-only. Three buttons:
 *   Seed   — creates / refreshes the demo project for the current tenant
 *   Reset  — wipes the demo project
 *   Verify — runs the loop verifier
 *
 * Hidden unless the env flag is set AND the user is on the
 * platform-admin allowlist.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sprout, RefreshCw, Trash2, ShieldCheck, AlertOctagon, Loader2,
  ArrowRight, CheckCircle2, XCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { confirm } from "@/lib/confirm";


export default function DemoAdmin() {
  const [status, setStatus] = useState(null);
  const [seed, setSeed] = useState(null);
  const [verify, setVerify] = useState(null);
  const [working, setWorking] = useState(null);

  useEffect(() => {
    api.get("/admin/demo/status").then((r) => setStatus(r.data));
    api.get("/admin/demo/verify").then((r) => setVerify(r.data)).catch(() => {});
  }, []);

  const doSeed = async (force) => {
    setWorking("seed");
    try {
      const r = await api.post("/admin/demo/seed", { force });
      setSeed(r.data);
      const v = await api.get("/admin/demo/verify");
      setVerify(v.data);
      confirm.raw?.success?.(force ? "Demo re-seeded" : "Demo seeded");
    } catch (e) {
      confirm.raw?.error?.(e?.response?.data?.detail || "Seed failed");
    } finally {
      setWorking(null);
    }
  };

  const doReset = async () => {
    setWorking("reset");
    try {
      const r = await api.post("/admin/demo/reset", {});
      setSeed(null);
      setVerify(null);
      confirm.raw?.success?.(r.data?.removed ? "Demo removed" : "Nothing to remove");
    } catch (e) {
      confirm.raw?.error?.(e?.response?.data?.detail || "Reset failed");
    } finally {
      setWorking(null);
    }
  };

  const doVerify = async () => {
    setWorking("verify");
    try {
      const r = await api.get("/admin/demo/verify");
      setVerify(r.data);
    } catch (e) {
      confirm.raw?.error?.(e?.response?.data?.detail || "Verify failed");
    } finally {
      setWorking(null);
    }
  };

  if (!status) {
    return <div className="p-8 text-sm text-[var(--fop-text-3)]" data-testid="demo-admin-loading">Loading…</div>;
  }
  if (!status.env_flag_enabled) {
    return (
      <div className="p-10 max-w-xl mx-auto text-center" data-testid="demo-admin-blocked">
        <AlertOctagon size={32} className="mx-auto text-[var(--fop-warning)]" />
        <div className="mt-2 font-semibold text-[var(--fop-text)]">Demo surface disabled</div>
        <div className="mt-1 text-sm text-[var(--fop-text-3)]">
          This environment does not have the demo flag enabled. Set
          {" "}<code className="font-mono">ENABLE_DEMO_SEED=true</code>{" "}
          in the backend env and restart to use this surface.
        </div>
      </div>
    );
  }
  if (!status.is_platform_admin) {
    return (
      <div className="p-10 max-w-xl mx-auto text-center" data-testid="demo-admin-no-permission">
        <ShieldCheck size={32} className="mx-auto text-[var(--fop-text-3)]" />
        <div className="mt-2 font-semibold text-[var(--fop-text)]">Platform admin required</div>
        <div className="mt-1 text-sm text-[var(--fop-text-3)]">
          Only platform admins can run the demo workflow.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="demo-admin">
      <div className="mb-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-primary)]">
          Pillar 7 · Demo Admin
        </div>
        <h1 className="text-2xl font-semibold text-[var(--fop-text)]" data-testid="demo-admin-title">
          Demo Project — Sample Workflow
        </h1>
        <p className="text-sm text-[var(--fop-text-3)] mt-1">
          Build the full 90-second demo loop in this tenant. Idempotent. Safe to re-run.
          Demo data is tagged so it never mixes with production.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-6">
        <button
          onClick={() => doSeed(false)}
          disabled={!!working}
          className="min-h-[64px] px-3 py-2 rounded border border-[var(--fop-primary)]/60 bg-[var(--fop-primary-soft)] text-[var(--fop-text)] flex items-center gap-2 text-sm font-semibold disabled:opacity-50"
          data-testid="demo-admin-seed"
        >
          {working === "seed" ? <Loader2 size={18} className="animate-spin" /> : <Sprout size={18} />}
          <span className="flex-1 text-left">Seed demo</span>
        </button>
        <button
          onClick={() => doSeed(true)}
          disabled={!!working}
          className="min-h-[64px] px-3 py-2 rounded border border-[var(--fop-warning)]/55 bg-[var(--fop-surface)] hover:bg-[var(--fop-surface-2)] text-[var(--fop-text)] flex items-center gap-2 text-sm font-semibold disabled:opacity-50"
          data-testid="demo-admin-reseed"
        >
          {working === "seed" ? <Loader2 size={18} className="animate-spin" /> : <RefreshCw size={18} />}
          <span className="flex-1 text-left">Refresh demo</span>
        </button>
        <button
          onClick={doReset}
          disabled={!!working}
          className="min-h-[64px] px-3 py-2 rounded border border-[var(--fop-danger,#dc2626)]/55 bg-[var(--fop-surface)] hover:bg-[var(--fop-surface-2)] text-[var(--fop-text)] flex items-center gap-2 text-sm font-semibold disabled:opacity-50"
          data-testid="demo-admin-reset"
        >
          {working === "reset" ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
          <span className="flex-1 text-left">Remove demo</span>
        </button>
      </div>

      {/* Seed summary */}
      {(seed || verify?.seed_status) && (
        <div className="mb-5 p-3 rounded border border-[var(--fop-border)] bg-[var(--fop-surface)]" data-testid="demo-admin-seed-summary">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">
            Demo project
          </div>
          <div className="font-semibold text-[var(--fop-text)]">
            {(seed?.project_name) || verify?.seed_status?.project_name}
          </div>
          <div className="mt-2 grid grid-cols-3 gap-3 text-[12px]">
            {Object.entries(((seed?.counts) || verify?.seed_status?.counts) || {}).map(([k, v]) => (
              <div key={k}>
                <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text-3)]">
                  {k.replace(/_/g, " ")}
                </div>
                <div className="font-semibold">{v}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link to={(seed?.war_room_url) || verify?.seed_status?.war_room_url || "#"} className="btn btn-primary text-[12px] inline-flex items-center gap-1" data-testid="demo-admin-open-warroom">
              <ArrowRight size={12} /> Open War Room
            </Link>
            <Link to="/inbox" className="text-[12px] text-[var(--fop-text-2)] hover:text-[var(--fop-text)]" data-testid="demo-admin-open-inbox">Open Inbox →</Link>
            <Link to={(seed?.command_view_url) || verify?.seed_status?.command_view_url || "/command"} className="text-[12px] text-[var(--fop-text-2)] hover:text-[var(--fop-text)]" data-testid="demo-admin-open-command">Open Command View →</Link>
          </div>
        </div>
      )}

      {/* Verifier output */}
      <div className="rounded border border-[var(--fop-border)] bg-[var(--fop-surface)] overflow-hidden">
        <div className="px-3 py-2 border-b border-[var(--fop-border)] flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">
            Loop Verifier
          </span>
          <button
            onClick={doVerify}
            disabled={!!working}
            className="ml-auto inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-2)] hover:text-[var(--fop-text)] disabled:opacity-50"
            data-testid="demo-admin-verify"
          >
            <RefreshCw size={11} className={working === "verify" ? "animate-spin" : ""} /> Run again
          </button>
        </div>
        {!verify ? (
          <div className="p-4 text-sm text-[var(--fop-text-3)]">No verifier output yet — click Run again.</div>
        ) : !verify.steps?.length ? (
          <div className="p-4 text-sm text-[var(--fop-text-3)]" data-testid="demo-admin-verify-empty">
            {verify.summary}
          </div>
        ) : (
          <>
            <div
              className={`px-3 py-2 text-[12px] font-semibold ${
                verify.ok ? "text-[var(--fop-success)]" : "text-[var(--fop-danger,#dc2626)]"
              }`}
              data-testid="demo-admin-verify-summary"
            >
              {verify.ok ? <CheckCircle2 size={14} className="inline mr-1" /> : <XCircle size={14} className="inline mr-1" />}
              {verify.summary}
            </div>
            <ol className="divide-y divide-[var(--fop-border)]">
              {verify.steps.map((s) => (
                <li
                  key={s.name}
                  className="px-3 py-2 text-[12px] flex items-start gap-2"
                  data-testid={`demo-admin-step-${s.name}`}
                  data-status={s.status}
                >
                  {s.status === "pass"
                    ? <CheckCircle2 size={14} className="text-[var(--fop-success)] mt-0.5" />
                    : <XCircle size={14} className="text-[var(--fop-danger,#dc2626)] mt-0.5" />}
                  <span className="flex-1">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text-3)] block">
                      {s.name.replace(/_/g, " ")}
                    </span>
                    <span className="text-[var(--fop-text-2)]">{s.reason}</span>
                  </span>
                </li>
              ))}
            </ol>
          </>
        )}
      </div>
    </div>
  );
}
